import type { PlayerIndex } from "./entities";
import type { PlayerState, WorldState } from "./state";

/**
 * How a match ended. The original never implemented this (`triggerGameOver()` was empty);
 * the rule here: a player whose last ship is destroyed is eliminated. With several players
 * the last one standing wins (all eliminated on the same tick = draw); a solo run ends
 * when its pilot is eliminated.
 */
export type MatchResult =
	| { kind: "winner"; winner: PlayerIndex }
	| { kind: "draw" }
	| { kind: "solo"; player: PlayerIndex };

export function isEliminated(p: PlayerState): boolean {
	return p.shipsLeft <= 0 && p.shipId === null && !p.awaitingSpawn;
}

/** The match result, or null while the match is still undecided. */
export function matchResult(world: WorldState): MatchResult | null {
	const { players } = world;
	if (players.length === 0) return null;

	// Objective modes: first to the target score (several on the same tick = draw).
	const objective = world.objective;
	if (objective && objective.kind !== "survival") {
		const done = players.filter((p) => (objective.scores[p.index] ?? 0) >= objective.target);
		if (done.length === 0) return null;
		return done.length === 1 && done[0]
			? { kind: "winner", winner: done[0].index }
			: { kind: "draw" };
	}

	const solo = players[0];
	if (players.length === 1 && solo) {
		return isEliminated(solo) ? { kind: "solo", player: solo.index } : null;
	}

	const alive = players.filter((p) => !isEliminated(p));
	if (alive.length > 1) return null;
	const winner = alive[0];
	return winner ? { kind: "winner", winner: winner.index } : { kind: "draw" };
}
